import moment from 'moment';
import { useState } from 'react';
import { VodafoneTariffData } from '@tymbe/schema/vodafone-tariff.interface';
import { VodafoneTariffApplicationType, SimType, VodafoneTariffState, VodafoneTariffPaymentState } from '@tymbe/schema/enums';
import { TyTableRow, TyTableCell } from '@tymbe/ty-components/table';
import { PrimaryButton, SecondaryButton } from '@tymbe/ui-primitives';
import { SideModal } from '@tymbe/ty-components/modals';
import { TyInput } from '@tymbe/ty-components/inputs';
import { Form } from 'informed';
import { isValidPhoneNumber } from '@tymbe/utils/validations';
import AlertModal from '../../../../components/modals/AlertModal';

type VodafoneTariffRowProps = {
  vodafoneTariff: VodafoneTariffData;
  visibleColumns: string[];
  tabName?: string;
  onStateChange?: (id: number, newState: VodafoneTariffState, transferDate?: string | null, phoneNumber?: string | null, transferRequested?: string | null) => void;
  onRowClick?: (id: number) => void;
};

const getApplicationTypeLabel = (type: VodafoneTariffApplicationType) => {
  switch (type) {
    case VodafoneTariffApplicationType.NEW_PHONE_NUMBER:
      return 'Nové číslo';
    case VodafoneTariffApplicationType.VODAFONE_TRANSFER:
      return 'Přenos z Vodafone';
    case VodafoneTariffApplicationType.DIFFERENT_OPERATOR_TRANSFER:
      return 'Přenos z jiného operátora';
    default:
      return type;
  }
};

const getSimTypeLabel = (type: SimType) => {
  switch (type) {
    case SimType.ESIM:
      return 'eSIM';
    case SimType.PHYSICAL_SIM:
      return 'Fyzická SIM';
    case SimType.EXISTING_SIM:
      return 'Stávající SIM';
    default:
      return type;
  }
};

// Utility function to extract the oldest pending period
const getOldestPendingPeriod = (vodafoneTariff: VodafoneTariffData): string | null => {
  if (!vodafoneTariff.vodafoneTariffPayments?.length) return null;

  const pendingPayments = vodafoneTariff.vodafoneTariffPayments
    .filter(payment => payment.state === VodafoneTariffPaymentState.PENDING_PAYMENT)
    .sort((a, b) => {
      return a.period_month.localeCompare(b.period_month);
    });

  return pendingPayments[0]?.period_month || null;
};

// Utility function to format period for display as MM. YYYY
const formatPeriod = (period: string): string => {
  const date = new Date(period);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}. ${year}`;
};

export const VodafoneTariffRow = ({ vodafoneTariff, visibleColumns, tabName, onStateChange, onRowClick }: VodafoneTariffRowProps) => {
  const [showTransferDateModal, setShowTransferDateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showCancelTransferConfirm, setShowCancelTransferConfirm] = useState(false);
  const [showCancelActivationConfirm, setShowCancelActivationConfirm] = useState(false);

  const handleStateChange = (newState: VodafoneTariffState, transferDate?: string | null, phoneNumber?: string | null, transferRequested?: string | null) => {
    if (onStateChange) {
      onStateChange(vodafoneTariff.id, newState, transferDate, phoneNumber, transferRequested);
    }
  };

  const handleActivateClick = () => {
    setShowTransferDateModal(true);
  };

  const handleToActivationClick = () => {
    const now = new Date().toISOString();
    handleStateChange(VodafoneTariffState.REQUESTED_TRANSFER, undefined, undefined, now);
  };

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = () => {
    handleStateChange(VodafoneTariffState.CANCELLED);
    setShowCancelConfirm(false);
  };

  const handleDeactivateClick = () => {
    setShowDeactivateConfirm(true);
  };

  const handleConfirmDeactivate = () => {
    handleStateChange(VodafoneTariffState.DEACTIVATED);
    setShowDeactivateConfirm(false);
  };

  const handleCancelTransferClick = () => {
    setShowCancelTransferConfirm(true);
  };

  const handleConfirmCancelTransfer = () => {
    handleStateChange(VodafoneTariffState.PAYMENT_SUCCESS);
    setShowCancelTransferConfirm(false);
  };

  const handleCancelActivationClick = () => {
    setShowCancelActivationConfirm(true);
  };

  const handleConfirmCancelActivation = () => {
    const phoneNumber = vodafoneTariff.application_type === VodafoneTariffApplicationType.NEW_PHONE_NUMBER ? null : undefined;
    handleStateChange(VodafoneTariffState.REQUESTED_TRANSFER, null, phoneNumber, null);
    setShowCancelActivationConfirm(false);
  };

  const handleTransferDateSubmit = async (formState: { values: { transfer_date: string; phone_number?: string } }) => {
    setIsSubmitting(true);
    try {
      // Validate phone number if provided
      if (formState.values.phone_number && !isValidPhoneNumber(formState.values.phone_number)) {
        throw new Error('Neplatný formát telefonního čísla. Použijte formát +420xxxxxxxxx');
      }

      await handleStateChange(VodafoneTariffState.ACTIVE, formState.values.transfer_date, formState.values.phone_number);
      setShowTransferDateModal(false);
    } catch (error) {
      // Error handled by parent component or show validation error
      console.error('Validation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTodayDate = () => {
    return moment().format('YYYY-MM-DD');
  };

  const getAvailableActions = () => {
    const actions = [];

    // Special handling for overdue-payments tab
    if (tabName === 'overdue-payments') {
      // For overdue payments, all tariffs are ACTIVE (filtered by API) and we only show deactivate action
      actions.push(
        <SecondaryButton
          key="deactivate"
          onClick={handleDeactivateClick}
          variant="outlined-error"
        >
          Deaktivovat
        </SecondaryButton>
      );
      return actions;
    }

    // Default behavior for other tabs
    switch (vodafoneTariff.state) {
      case VodafoneTariffState.PAYMENT_PENDING:
        actions.push(
          <SecondaryButton
            key="cancel"
            onClick={handleCancelClick}
            variant="outlined-error"
          >
            Zrušit tarif
          </SecondaryButton>
        );
        break;

      case VodafoneTariffState.PAYMENT_SUCCESS:
        actions.push(
          <PrimaryButton
            key="to-activation"
            onClick={handleToActivationClick}
          >
            K aktivaci
          </PrimaryButton>
        );
        actions.push(
          <SecondaryButton
            key="cancel"
            onClick={handleCancelClick}
            variant="outlined-error"
          >
            Zrušit tarif
          </SecondaryButton>
        );
        break;

      case VodafoneTariffState.REQUESTED_TRANSFER:
        actions.push(
          <PrimaryButton
            key="activate"
            onClick={handleActivateClick}
          >
            Aktivováno
          </PrimaryButton>
        );
        actions.push(
          <SecondaryButton
            key="cancel-transfer"
            onClick={handleCancelTransferClick}
          >
            Zrušit předání
          </SecondaryButton>
        );
        actions.push(
          <SecondaryButton
            key="cancel"
            onClick={handleCancelClick}
            variant="outlined-error"
          >
            Zrušit tarif
          </SecondaryButton>
        );
        break;

      case VodafoneTariffState.ACTIVE:
        actions.push(
          <SecondaryButton
            key="cancel-activation"
            onClick={handleCancelActivationClick}
          >
            Zrušit aktivaci
          </SecondaryButton>
        );
        actions.push(
          <SecondaryButton
            key="cancel"
            onClick={handleCancelClick}
            variant="outlined-error"
          >
            Zrušit tarif
          </SecondaryButton>
        );
        actions.push(
          <SecondaryButton
            key="deactivate"
            onClick={handleDeactivateClick}
            variant="outlined-error"
          >
            Deaktivovat
          </SecondaryButton>
        );
        break;

      case VodafoneTariffState.CANCELLED:
        actions.push(
          <PrimaryButton
            key="reactivate"
            onClick={() => handleStateChange(VodafoneTariffState.ACTIVE)}
          >
            Aktivovat
          </PrimaryButton>
        );
        break;

      case VodafoneTariffState.DEACTIVATED:
        actions.push(
          <PrimaryButton
            key="reactivate"
            onClick={() => handleStateChange(VodafoneTariffState.ACTIVE)}
          >
            Aktivovat
          </PrimaryButton>
        );
        break;
    }

    return actions;
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onRowClick?.(vodafoneTariff.id);
  };

  const renderCell = (columnName: string) => {
    switch (columnName) {
      case 'id':
        return <TyTableCell key="id">{vodafoneTariff.id}</TyTableCell>;
      case 'person_id':
        return <TyTableCell key="person_id">{vodafoneTariff.person_id}</TyTableCell>;
      case 'phone_number':
        return <TyTableCell key="phone_number">{vodafoneTariff.phone_number || 'N/A'}</TyTableCell>;
      case 'email':
        return <TyTableCell key="email">{vodafoneTariff.email}</TyTableCell>;
      case 'first_name':
        return <TyTableCell key="first_name">{vodafoneTariff.first_name}</TyTableCell>;
      case 'last_name':
        return <TyTableCell key="last_name">{vodafoneTariff.last_name}</TyTableCell>;
      case 'application_type':
        return <TyTableCell key="application_type">{getApplicationTypeLabel(vodafoneTariff.application_type)}</TyTableCell>;
      case 'sim_type':
        return <TyTableCell key="sim_type">{getSimTypeLabel(vodafoneTariff.sim_type)}</TyTableCell>;
      case 'fakturoid_subject_id':
        return <TyTableCell key="fakturoid_subject_id">{vodafoneTariff.fakturoid_subject_id || 'N/A'}</TyTableCell>;
      case 'oldest_pending_period': {
        const oldestPeriod = getOldestPendingPeriod(vodafoneTariff);
        return <TyTableCell key="oldest_pending_period">{oldestPeriod ? formatPeriod(oldestPeriod) : 'N/A'}</TyTableCell>;
      }
      case 'transfer_date':
        return <TyTableCell key="transfer_date">{vodafoneTariff.transfer_date ? moment(vodafoneTariff.transfer_date).format('L') : 'N/A'}</TyTableCell>;
      case 'created_at':
        return <TyTableCell key="created_at">{moment(vodafoneTariff.created_at).format('L')}</TyTableCell>;
      case 'note':
        return (
          <TyTableCell key="note">
            {vodafoneTariff.note ? (
              <div
                className="truncate max-w-[200px] cursor-help"
                title={vodafoneTariff.note}
              >
                {vodafoneTariff.note}
              </div>
            ) : (
              'N/A'
            )}
          </TyTableCell>
        );
      case 'transfer_requested':
        return <TyTableCell key="transfer_requested">{vodafoneTariff.transfer_requested ? moment(vodafoneTariff.transfer_requested).format('DD. MM. YYYY') : 'N/A'}</TyTableCell>;
      case 'actions':
        return (
          <TyTableCell key="actions">
            <div className="flex gap-2 flex-wrap">
              {getAvailableActions()}
            </div>
          </TyTableCell>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TyTableRow
        className="cursor-pointer hover:bg-gray-50"
        onClick={handleRowClick}
      >
        {visibleColumns.map((columnName) => renderCell(columnName))}
      </TyTableRow>

      <SideModal
        isOpen={showTransferDateModal}
        onRequestClose={() => setShowTransferDateModal(false)}
        title={
          vodafoneTariff.application_type === VodafoneTariffApplicationType.NEW_PHONE_NUMBER
            ? "Aktivace - Datum a telefonní číslo"
            : "Aktivace - Datum přenosu čísla"
        }
      >
        <div className="p-6">
          <Form onSubmit={handleTransferDateSubmit}>
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  {vodafoneTariff.application_type === VodafoneTariffApplicationType.NEW_PHONE_NUMBER
                    ? "Zadejte datum aktivace."
                    : "Zadejte datum, kdy má být provedeno přenesení čísla."
                  }
                </p>
                <TyInput
                  name="transfer_date"
                  label={
                    vodafoneTariff.application_type === VodafoneTariffApplicationType.NEW_PHONE_NUMBER
                      ? "Datum aktivace"
                      : "Datum přenosu"
                  }
                  type="date"
                  required
                  initialValue={getTodayDate()}
                  prefixElement={null}
                />
              </div>

              {vodafoneTariff.application_type === VodafoneTariffApplicationType.NEW_PHONE_NUMBER && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Jelikož se jedná o nové číslo, zadejte prosím telefonní číslo, které bude aktivováno.
                  </p>
                  <TyInput
                    name="phone_number"
                    label="Telefonní číslo"
                    type="tel"
                    required
                    placeholder="+420xxxxxxxxx"
                    prefixElement={null}
                    validate={(value) => {
                      if (!value) return 'Telefonní číslo je povinné';
                      if (!isValidPhoneNumber(String(value))) {
                        return 'Neplatný formát telefonního čísla. Použijte formát +420xxxxxxxxx';
                      }
                    }}
                  />
                </div>
              )}

              <div className="flex justify-end pt-4">
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Aktivuji...' : 'Aktivovat'}
                </PrimaryButton>
              </div>
            </div>
          </Form>
        </div>
      </SideModal>

      <AlertModal
        title="Potvrdit zrušení"
        message={`Opravdu chcete zrušit vodafone tarif pro ${vodafoneTariff.first_name} ${vodafoneTariff.last_name}?`}
        showModal={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onYes={handleConfirmCancel}
        yesButtonText="Ano, zrušit"
        cancelButtonText="Zrušit"
      />

      <AlertModal
        title="Potvrdit deaktivaci"
        message={`Opravdu chcete deaktivovat vodafone tarif pro ${vodafoneTariff.first_name} ${vodafoneTariff.last_name}? Tato akce označí tarif jako deaktivovaný z důvodu neplatičství.`}
        showModal={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        onYes={handleConfirmDeactivate}
        yesButtonText="Ano, deaktivovat"
        cancelButtonText="Zrušit"
      />

      <AlertModal
        title="Potvrdit zrušení předání"
        message={`Opravdu chcete zrušit předání k aktivaci pro ${vodafoneTariff.first_name} ${vodafoneTariff.last_name}? Tarif se vrátí do stavu "Platba úspěšná".`}
        showModal={showCancelTransferConfirm}
        onClose={() => setShowCancelTransferConfirm(false)}
        onYes={handleConfirmCancelTransfer}
        yesButtonText="Ano, zrušit předání"
        cancelButtonText="Zrušit"
      />

      <AlertModal
        title="Potvrdit zrušení aktivace"
        message={`Opravdu chcete zrušit aktivaci pro ${vodafoneTariff.first_name} ${vodafoneTariff.last_name}? Tarif se vrátí do stavu "Předáno k aktivaci" a vymaže se datum přenosu${vodafoneTariff.application_type === VodafoneTariffApplicationType.NEW_PHONE_NUMBER ? ' a telefonní číslo' : ''}.`}
        showModal={showCancelActivationConfirm}
        onClose={() => setShowCancelActivationConfirm(false)}
        onYes={handleConfirmCancelActivation}
        yesButtonText="Ano, zrušit aktivaci"
        cancelButtonText="Zrušit"
      />
    </>
  );
};
